import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FileText, Trash2, Check, RotateCcw, ChevronDown, Send } from "lucide-react";

const FIELDS = [
  ["title", "Title", "input"],
  ["brief", "Brief", "textarea"],
  ["category", "Category", "input"],
  ["project", "Project", "input"],
  ["genre_style", "Genre / Style", "input"],
  ["vocals", "Vocals", "input"],
  ["tempo_energy", "Tempo / Energy", "input"],
  ["delivery_notes", "Delivery notes", "textarea"],
  ["coach_note", "Coach's note", "textarea"],
];

export const AdminBriefs = () => {
  const [briefs, setBriefs] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [text, setText] = useState("");
  const [mode, setMode] = useState("append");
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [edit, setEdit] = useState({});

  const load = () => api.get("/admin/briefs").then((r) => { setBriefs(r.data.briefs); setStartDate(r.data.start_date); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const doImport = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const res = await api.post("/admin/briefs/bulk", { text, mode });
      toast.success(`Imported ${res.data.imported} briefs — ${res.data.total} total in the machine.`);
      setText("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const saveBrief = async (b) => {
    try {
      await api.put(`/admin/briefs/${b.id}`, edit);
      toast.success(`Week ${b.week} updated.`);
      setOpenId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const deleteBrief = async (b) => {
    if (!window.confirm(`Delete Week ${b.week} — "${b.title}"?`)) return;
    try { await api.delete(`/admin/briefs/${b.id}`); toast.success("Brief deleted."); load(); }
    catch { toast.error("Delete failed"); }
  };

  const resetClock = async () => {
    if (!window.confirm("Restart the global clock? Brief #1 becomes this week's brief for everyone.")) return;
    try {
      const d = new Date();
      d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      d.setHours(0, 0, 0, 0);
      await api.put("/admin/briefs/config", { start_date: d.toISOString() });
      toast.success("Clock restarted — Week 1 is live now (weeks flip on Mondays).");
      load();
    } catch { toast.error("Could not reset clock"); }
  };

  const sendDrop = async () => {
    if (!window.confirm("Email this week's brief to every paid member right now?")) return;
    try {
      const res = await api.post("/admin/send-brief-drop", { dry_run: false });
      toast.success(res.data.reason ? `Skipped — ${res.data.reason}.` : `Sent to ${res.data.sent} of ${res.data.eligible} paid members.`);
    } catch { toast.error("Send failed"); }
  };

  const toggleRow = (b) => {
    if (openId === b.id) { setOpenId(null); return; }
    setOpenId(b.id);
    setEdit({ title: b.title, brief: b.brief || b.body, category: b.category, project: b.project, genre_style: b.genre_style, vocals: b.vocals, tempo_energy: b.tempo_energy, delivery_notes: b.delivery_notes, coach_note: b.coach_note });
  };

  return (
    <div data-testid="admin-briefs-panel" className="border border-line bg-surface p-6 md:p-8 mb-8">
      <div className="flex items-center gap-2 font-heading text-2xl font-bold uppercase text-cream tracking-tight mb-2"><FileText size={20} className="text-gold" /> The Brief Machine</div>
      <p className="text-ash text-sm mb-5">
        One brief per week, global for all paid members. Paste briefs below — each must start with a line like <span className="text-gold font-mono">WEEK 1</span>, followed by <span className="font-mono">Title:</span>, <span className="font-mono">Brief:</span>, <span className="font-mono">Genre:</span>, <span className="font-mono">Mood/Tempo:</span>, <span className="font-mono">References:</span>, <span className="font-mono">Original Deadline:</span> lines.
      </p>

      <textarea
        data-testid="briefs-bulk-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={7}
        placeholder={"WEEK 1\nTitle: Uptempo Female Pop for Asian Market\nBrief: Seeking an uptempo, confident pop record…\nGenre: Pop\nMood/Tempo: Uptempo, 110-125bpm\nReferences: Dua Lipa, NewJeans\nOriginal Deadline: March 2025 (expired)\n\nWEEK 2\n…"}
        className="w-full bg-ink border border-line text-cream text-sm px-4 py-3 font-mono focus:outline-none focus:border-gold placeholder:text-ash/40 mb-3"
      />
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex border border-line font-mono text-[11px] uppercase tracking-wide">
          <button onClick={() => setMode("append")} data-testid="briefs-mode-append" className={`px-4 py-2 ${mode === "append" ? "bg-gold text-ink font-bold" : "text-ash hover:text-cream"}`}>Add to end</button>
          <button onClick={() => setMode("replace")} data-testid="briefs-mode-replace" className={`px-4 py-2 ${mode === "replace" ? "bg-gold text-ink font-bold" : "text-ash hover:text-cream"}`}>Replace all</button>
        </div>
        <button onClick={doImport} disabled={busy || !text.trim()} data-testid="briefs-import-btn" className="inline-flex items-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-gold-hi transition-colors disabled:opacity-50">
          <Check size={14} /> {busy ? "Importing…" : "Import briefs"}
        </button>
        {startDate && (
          <div className="flex items-center gap-3 ml-auto">
            <button onClick={sendDrop} data-testid="briefs-send-drop-btn" className="inline-flex items-center gap-2 border border-line text-ash font-mono text-[11px] uppercase tracking-wide px-4 py-2.5 hover:text-cream hover:border-gold transition-colors">
              <Send size={13} /> Email this week's brief now
            </button>
            <button onClick={resetClock} data-testid="briefs-reset-clock-btn" className="inline-flex items-center gap-2 border border-line text-ash font-mono text-[11px] uppercase tracking-wide px-4 py-2.5 hover:text-cream hover:border-gold transition-colors">
              <RotateCcw size={13} /> Restart clock (Week 1 = now)
            </button>
          </div>
        )}
      </div>

      {briefs.length === 0 ? (
        <p className="text-ash text-sm border border-dashed border-line p-4">The machine is empty — paste your briefs above to stock it.</p>
      ) : (
        <div className="max-h-[420px] overflow-y-auto border border-line divide-y divide-line">
          {briefs.map((b) => (
            <div key={b.id} className="bg-ink">
              <div className="flex items-center gap-3 p-3">
                <span className="font-mono text-[11px] text-gold w-12 shrink-0">WK {String(b.week).padStart(2, "0")}</span>
                <button onClick={() => toggleRow(b)} data-testid={`admin-brief-row-${b.week}`} className="flex-1 flex items-center gap-2 text-left text-sm text-cream truncate hover:text-gold transition-colors">
                  <span className="truncate">{b.title || "(untitled)"}</span>
                  <ChevronDown size={13} className={`text-ash shrink-0 transition-transform ${openId === b.id ? "rotate-180" : ""}`} />
                </button>
                <button onClick={() => deleteBrief(b)} data-testid={`admin-brief-delete-${b.week}`} className="text-ash hover:text-destructive p-1" aria-label="Delete brief"><Trash2 size={14} /></button>
              </div>
              {openId === b.id && (
                <div className="px-3 pb-4 flex flex-col gap-3">
                  {FIELDS.map(([key, label, kind]) => (
                    <div key={key}>
                      <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-ash block mb-1">{label}</label>
                      {kind === "textarea" ? (
                        <textarea value={edit[key] || ""} onChange={(e) => setEdit((p) => ({ ...p, [key]: e.target.value }))} rows={5} className="w-full bg-surface border border-line text-cream text-sm px-3 py-2 focus:outline-none focus:border-gold" />
                      ) : (
                        <input value={edit[key] || ""} onChange={(e) => setEdit((p) => ({ ...p, [key]: e.target.value }))} className="w-full bg-surface border border-line text-cream text-sm px-3 py-2 focus:outline-none focus:border-gold" />
                      )}
                    </div>
                  ))}
                  <button onClick={() => saveBrief(b)} data-testid={`admin-brief-save-${b.week}`} className="self-start inline-flex items-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-xs px-5 py-2.5 hover:bg-gold-hi transition-colors"><Check size={13} /> Save Week {b.week}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
